/**
 * RecycleBinCard — shared "Recently deleted (30-day undo)" panel.
 * Used on Appointments (kind="appointment") and Finances (kind="invoice").
 */
import React, { useEffect, useState } from 'react';
import { Clock, RotateCcw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { safeConfirm } from '../lib/confirm';
import { toast } from 'sonner';

export default function RecycleBinCard({ kind, noun, onRestored }) {
  const [rows, setRows] = useState([]);

  const load = () => api.get(`/recycle-bin?kind=${kind}`).then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (r) => {
    try {
      const { data } = await api.post(`/recycle-bin/${r.item_id}/restore`);
      if (data.slot_conflict) {
        toast.warning(`${noun} restored — but its time slot now overlaps another booking. Please reschedule it.`);
      } else {
        toast.success(`${noun} restored.`);
      }
      load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed.');
    }
  };

  const eraseNow = async (r) => {
    if (!(await safeConfirm(`PERMANENTLY erase "${r.label}" right now? This cannot be undone.`))) return;
    try {
      await api.delete(`/recycle-bin/${r.item_id}`);
      toast.success('Erased permanently.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erase failed.');
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="admin-card mt-6 border-[#C89B3C]/40" data-testid={`recycle-bin-${kind}`}>
      <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-[#7A5500]"/> Recently deleted
        <span className="text-xs font-normal text-fg-muted">— restorable for 30 days, then erased permanently</span>
      </h3>
      <table className="mt-2">
        <thead>
          <tr><th>{noun}</th><th>Deleted</th><th>Deleted by</th><th>Time left</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.item_id} data-testid={`bin-row-${r.item_id}`}>
              <td className="font-medium">{r.label}</td>
              <td className="text-xs">{(r.deleted_at || '').slice(0, 10)}</td>
              <td className="text-xs text-fg-muted">{r.deleted_by_name || '—'}</td>
              <td className="text-xs">
                <span className={`badge-neutral ${r.days_remaining <= 5 ? '!text-[#B83A3A]' : ''}`}>{r.days_remaining} days</span>
              </td>
              <td>
                <div className="flex items-center gap-3">
                  <button onClick={() => restore(r)} className="text-xs text-brand inline-flex items-center gap-1"
                          data-testid={`bin-restore-${r.item_id}`}>
                    <RotateCcw className="w-3.5 h-3.5"/> Restore
                  </button>
                  <button onClick={() => eraseNow(r)} className="text-xs text-[#B83A3A] inline-flex items-center gap-1"
                          data-testid={`bin-erase-${r.item_id}`}>
                    <Trash2 className="w-3.5 h-3.5"/> Erase now
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
